// Client-side media utilities.
// Compresses images via <canvas> to keep uploads under a target size and dimension.
// Videos cannot be re-encoded in-browser without heavy libraries (WebCodecs is not
// broadly stable in Feb 2026), so we return them unchanged with metadata for the UI
// to enforce size limits and surface friendly errors.

const dataUrlBytes = (dataUrl) => Math.round(dataUrl.length * 0.75);

async function loadImage(file) {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = rej;
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function draw(img, maxDim) {
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

/**
 * Compress an image File progressively (quality then dimensions) until it fits
 * targetKb. Always output JPEG unless the input is a PNG with transparency.
 * @returns {Promise<{dataUrl:string, bytes:number, originalBytes:number, mimeType:string, width:number, height:number}>}
 */
export async function compressImageFile(file, opts = {}) {
  const { maxDim = 1920, minDim = 800, targetKb = 1400, initialQuality = 0.85 } = opts;
  const img = await loadImage(file);
  const originalBytes = file.size;

  const tryOnce = (dim, quality, mime = "image/jpeg") => {
    const canvas = draw(img, dim);
    const dataUrl = canvas.toDataURL(mime, quality);
    return { dataUrl, bytes: dataUrlBytes(dataUrl), width: canvas.width, height: canvas.height, mimeType: mime };
  };

  // First pass: fixed dim, decreasing quality.
  let quality = initialQuality;
  let out = tryOnce(maxDim, quality);
  while (out.bytes > targetKb * 1024 && quality > 0.45) {
    quality = Math.max(0.45, quality - 0.1);
    out = tryOnce(maxDim, quality);
  }
  // Second pass: shrink dimensions.
  let dim = maxDim;
  while (out.bytes > targetKb * 1024 && dim > minDim) {
    dim -= 320;
    out = tryOnce(dim, 0.8);
  }
  return { ...out, originalBytes };
}

/**
 * High-level helper used by admin upload forms.
 * - Images: compressed via <canvas> to fit `imageTargetKb` (default 1400 KB).
 * - Videos: returned as-is with size validation against `videoMaxMb`.
 * Returns { media_url, media_type, originalBytes, finalBytes } or throws on failure.
 */
export async function prepareMediaForUpload(file, opts = {}) {
  const {
    imageTargetKb = 1400,
    imageMaxDim = 1920,
    videoMaxMb = 15,
  } = opts;
  if (!file) return null;
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");
  if (!isImage && !isVideo) {
    throw new Error("Le fichier doit être une image ou une vidéo.");
  }
  if (isImage) {
    const compressed = await compressImageFile(file, { maxDim: imageMaxDim, targetKb: imageTargetKb });
    return {
      media_url: compressed.dataUrl,
      media_type: "image",
      originalBytes: compressed.originalBytes,
      finalBytes: compressed.bytes,
      width: compressed.width,
      height: compressed.height,
    };
  }
  // Video path — validate size and encode base64 (no re-encoding).
  if (file.size > videoMaxMb * 1024 * 1024) {
    throw new Error(`Vidéo trop lourde (max ${videoMaxMb} Mo). Compressez-la avant l'envoi.`);
  }
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Lecture vidéo impossible"));
    r.readAsDataURL(file);
  });
  return {
    media_url: dataUrl,
    media_type: "video",
    originalBytes: file.size,
    finalBytes: dataUrlBytes(dataUrl),
  };
}

export const formatBytes = (n) => {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(2)} Mo`;
};
